import { Center, Loader } from "@mantine/core";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom";

import { NotFound } from "./NotFound";
import { NotPermitted } from "./NotPermitted";
import { hasAnyRole, type Role, type Surface, surfaceOf } from "./roles";
import { homeOf, safeNext, useSession } from "./session";

/** Signed-in area. Anonymous → /login?next=…; setup-only sessions → /2fa/setup and nowhere else. */
export function RequireSession() {
  const { state } = useSession();
  const location = useLocation();
  if (state.status === "setup_required") return <Navigate to="/2fa/setup" replace />;
  if (state.status !== "authenticated") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

/** `/admin/*` only for BO_OPERATOR, `/portal/*` only for PORTAL; the wrong surface is not-found. */
export function RequireSurface({ surface }: { surface: Surface }) {
  const { me } = useSession();
  return surfaceOf(me) === surface ? <Outlet /> : <NotFound />;
}

export function RequireRoles({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { me } = useSession();
  return hasAnyRole(me, roles) ? <>{children}</> : <NotPermitted />;
}

/** Login & co: a signed-in user is sent on (to `next`, or their surface home). */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { state, me } = useSession();
  const [params] = useSearchParams();
  if (state.status === "setup_required") return <Navigate to="/2fa/setup" replace />;
  if (state.status === "authenticated") return <Navigate to={safeNext(params.get("next")) ?? homeOf(me)} replace />;
  return <>{children}</>;
}

/** Enrolment screen: only for setup-only sessions. */
export function SetupOnly({ children }: { children: ReactNode }) {
  const { state, me } = useSession();
  if (state.status === "authenticated") return <Navigate to={homeOf(me)} replace />;
  if (state.status !== "setup_required") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function HomeRedirect() {
  const { me } = useSession();
  return me ?
      <Navigate to={homeOf(me)} replace />
    : <Center h="50vh">
        <Loader />
      </Center>;
}
